/**
 * @fileoverview Orders API functions
 * 
 * Handles order management including:
 * - Order CRUD operations
 * - Order status updates
 * - Order cancellation
 * - Dashboard data
 * - Menu summary
 * 
 * @module api/orders
 */

import apiClient, { 
  handleUnauthorizedResponse, 
  getAuthHeaders, 
  getRestaurantId 
} from './client';

// ============================================================================
// ORDER QUERIES
// ============================================================================

/**
 * Get orders with optional date and sector filters
 * @param {Object} router - Next.js router
 * @param {string} startDate - Start date filter
 * @param {string} endDate - End date filter
 * @param {string} sector - Sector filter
 * @returns {Promise} Orders list
 */
export const getOrder = async (router, startDate, endDate, sector, businessSessionIds = null) => {
  const restaurantId = getRestaurantId();
  try {
    const params = {
      start_date: startDate,
      end_date: endDate,
      sectors: sector,
    };
    // Comma-separated session id(s): one specific session, or the visible set
    // for the "All sessions" filter (keeps the result bounded).
    if (businessSessionIds !== null && businessSessionIds !== undefined && businessSessionIds !== '') {
      params.business_session_ids = businessSessionIds;
    }
    const response = await apiClient.get(`/admin/restaurant/${restaurantId}/order/`, {
      headers: getAuthHeaders(),
      params,
    });
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Orders for the Kitchen Display Screen. Unlike getOrder (the board query,
 * which hides PAID orders), this includes paid orders that still hold
 * PENDING/KITCHEN items — required in pay-first mode where orders are born
 * paid while the kitchen still has to cook them. Falls back to the classic
 * board query if the backend doesn't expose the endpoint yet.
 * @param {Object} router - router
 * @returns {Promise<Array>} kitchen-relevant orders
 */
export const getKitchenOrders = async (router) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.get(
      `/admin/restaurant/${restaurantId}/order/kitchen-display/`,
      { headers: getAuthHeaders() }
    );
    return Array.isArray(response?.data) ? response.data : [];
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
      throw error;
    }
    // Older backend (no kitchen-display route) → degrade to the board query.
    try {
      const res = await getOrder(router);
      const list = Array.isArray(res?.data) ? res.data : res?.data?.orders || [];
      return list;
    } catch (e) {
      throw error;
    }
  }
};

/** Where dishes are prepared (Cuisine, Bar…) — the kitchen screen's station filter. */
export const getKitchenStations = async () => {
  const restaurantId = getRestaurantId();
  const response = await apiClient.get(`/admin/restaurant/${restaurantId}/order/kitchen-display/stations/`,
    { headers: getAuthHeaders() });
  return Array.isArray(response?.data) ? response.data : [];
};

/**
 * Get a specific order by ID
 * @param {Object} router - Next.js router
 * @param {string} orderId - Order ID
 * @returns {Promise} Order details
 */
export const getOrderById = async (router, orderId) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.get(`/admin/restaurant/${restaurantId}/order/${orderId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Get paid orders
 * @param {Object} router - Next.js router
 * @param {string} startDate - Start date filter
 * @param {string} endDate - End date filter
 * @param {string} sector - Sector filter
 * @returns {Promise} Paid orders list
 */
export const orderPaid = async (router, startDate, endDate, sector, businessSessionIds = null) => {
  const restaurantId = getRestaurantId();
  try {
    const params = {
      start_date: startDate,
      end_date: endDate,
      sectors: sector,
    };
    // Comma-separated session id(s): one specific session, or the visible set
    // for the "All sessions" filter (keeps the result bounded).
    if (businessSessionIds !== null && businessSessionIds !== undefined && businessSessionIds !== '') {
      params.business_session_ids = businessSessionIds;
    }
    const response = await apiClient.get(`/admin/restaurant/${restaurantId}/order/paid/`, {
      headers: getAuthHeaders(),
      params,
    });
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Get order summary by table
 * @param {Object} router - Next.js router
 * @returns {Promise} Table order summary
 */
export const getTableSummary = async (router) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.get(`/admin/restaurant/${restaurantId}/OrderSummaryTable/`, {
      headers: getAuthHeaders(),
    });
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

// ============================================================================
// ============================================================================
// DASHBOARD & ANALYTICS
// ============================================================================

/**
 * Get dashboard data with filters
 * @param {Object} router - Next.js router
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @param {string} filterBy - Filter type
 * @param {string} sector - Sector filter
 * @param {boolean} allTime - Include all time data
 * @param {AbortSignal} signal - AbortController signal
 * @param {string|null} employeeId - Optional employee ID filter
 * @param {string} orderTypeView - Order type view (default: 'combined')
 * @returns {Promise} Dashboard data
 */
export const getDashboard = async (
  router,
  startDate,
  endDate,
  filterBy,
  sector,
  allTime,
  signal,
  employeeId = null,
  orderTypeView = 'combined',
  businessSessionIds = null,  // comma-separated string of session IDs, or null
  employeeIds = null,  // comma-separated string of employee IDs (multi-select), or null
  skip = {}  // { analytics, menus, employees }: blocks the caller does not read (returned empty, no scan)
) => {
  const restaurantId = getRestaurantId();
  try {
    const params = {
      filter_by: filterBy,
      start_date: startDate,
      end_date: endDate,
      sectors: sector,
      all_time: allTime,
      order_type_view: orderTypeView,
      ...(skip.analytics ? { skip_analytics: 1 } : {}),
      ...(skip.menus ? { skip_menus: 1 } : {}),
      ...(skip.employees ? { skip_employees: 1 } : {}),
    };

    // Multi-select employees take precedence; fall back to the single id.
    if (employeeIds !== null && employeeIds !== undefined && employeeIds !== '') {
      params.employee_ids = employeeIds;
    } else if (employeeId !== null && employeeId !== undefined) {
      params.employee_id = employeeId;
    }

    // Add business_session_ids to params if provided (scopes KPIs to selected sessions)
    if (businessSessionIds !== null && businessSessionIds !== undefined && businessSessionIds !== '') {
      params.business_session_ids = businessSessionIds;
    }

    const response = await apiClient.get(`/admin/restaurant/${restaurantId}/dashboard/`, {
      headers: getAuthHeaders(),
      params,
      signal,
      timeout: allTime ? 120000 : 30000,  // 2 minutes timeout for all-time view, 30s otherwise
    });
    return response;
  } catch (error) {
    // Don't handle AbortError as an actual error
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      return null;
    }

    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    } else if (error.response?.status === 500) {
      console.error('Internal Server Error:', error.response.data);
    } else {
      console.error('Error:', error.message);
    }
    throw error;
  }
};

/**
 * Fetch menu summary data
 * @param {Object} router - Next.js router
 * @param {string} endURL - API endpoint suffix
 * @param {AbortSignal} signal - AbortController signal
 * @returns {Promise} Menu summary data
 */
export const fetchMenuSummary = async (router, endURL, signal) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.get(
      `/admin/restaurant/${restaurantId}/dashboard/${endURL}`,
      {
        headers: getAuthHeaders(),
        signal,
      }
    );
    return response.data;
  } catch (error) {
    // Don't handle AbortError as an actual error
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      return null;
    }
    console.error('Failed to fetch menu summary:', error);
    throw error;
  }
};

// ============================================================================
// PIN MANAGEMENT (Dashboard Security)
// ============================================================================

/**
 * Get PIN status
 * @returns {Promise} PIN data
 */
export const getPin = async () => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.get(`/admin/restaurant/${restaurantId}/dashboard/get-pin/`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Set dashboard PIN
 * @param {Object} data - PIN data
 * @param {string} data.pin - The PIN to set
 * @returns {Promise} Set PIN response
 */
export const setPin = async (data) => {
  const restaurantId = getRestaurantId();
  const formData = new FormData();
  formData.append('pin', data.pin);

  try {
    const response = await apiClient.post(
      `/admin/restaurant/${restaurantId}/dashboard/set-pin/`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Verify dashboard PIN
 * @param {Object} data - PIN data
 * @param {string} data.pin - The PIN to verify
 * @returns {Promise} Verification response
 */
const APPROVAL_KEY = 'ods_manager_approval';
const APPROVAL_TTL_MS = 9 * 60 * 1000;

/** Header carrying the manager's PIN approval, when one was given in the last minutes. */
const managerApprovalHeader = () => {
  try {
    const a = JSON.parse(sessionStorage.getItem(APPROVAL_KEY) || 'null');
    if (a?.token && Date.now() - a.at < APPROVAL_TTL_MS) return { 'X-Manager-Approval': a.token };
  } catch { /* ignore */ }
  return {};
};

export const verifyPin = async (data) => {
  const restaurantId = getRestaurantId();
  const formData = new FormData();
  formData.append('pin', data.pin);

  try {
    const response = await apiClient.post(
      `/admin/restaurant/${restaurantId}/dashboard/verify-pin/`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );
    // The server hands back a short manager approval (10 min): the till sends it with a
    // void or a discount. The PIN itself is never kept.
    if (response.data?.approval_token) {
      try {
        sessionStorage.setItem(APPROVAL_KEY, JSON.stringify({ token: response.data.approval_token, at: Date.now() }));
      } catch { /* storage unavailable: the action will ask again */ }
    }
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ============================================================================
// ORDER OPERATIONS
// ============================================================================

/**
 * Edit an order
 * @param {Object} router - Next.js router
 * @param {string} orderId - Order ID
 * @param {Object} data - Order data
 * @returns {Promise} Updated order
 */
export const editOrder = async (router, orderId, data) => {
  const restaurantId = getRestaurantId();
  try {
    // Removing a line the kitchen already had (a loss) or emptying the order is a
    // manager's call: the till sends the manager's PIN approval when it has one.
    const response = await apiClient.put(
      `/admin/restaurant/${restaurantId}/order/${orderId}/`,
      data,
      { headers: { ...getAuthHeaders(), ...managerApprovalHeader() } }
    );
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Apply discount to an order
 * @param {Object} router - Next.js router
 * @param {string} orderId - Order ID
 * @param {number} discount - Discount amount
 * @param {string} employeeId - Employee ID
 * @returns {Promise} Updated order
 */
export const applyDiscount = async (router, orderId, discount, employeeId, orderMenuIds = null) => {
  const restaurantId = getRestaurantId();
  try {
    const body = { discount, employee_id: employeeId };
    // When provided, the discount is scoped to just these lines (one discount
    // group), leaving the order's other groups untouched. Omit => whole order.
    if (Array.isArray(orderMenuIds) && orderMenuIds.length > 0) {
      body.order_menu_ids = orderMenuIds;
    }
    const response = await apiClient.patch(
      `/admin/restaurant/${restaurantId}/order/${orderId}/discount/`,
      body,
      { headers: { ...getAuthHeaders(), ...managerApprovalHeader() } }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Mark order as paid
 * @param {Object} router - Next.js router
 * @param {string} orderId - Order ID
 * @param {Object} paymentData - Payment amounts data
 * @returns {Promise} Updated order
 */
export const markOrderAsPaid = async (router, orderId, paymentData, { idempotencyKey } = {}) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.patch(
      `/admin/restaurant/${restaurantId}/order/${orderId}/paid/`,
      paymentData,
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
          ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
        },
      }
    );
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Mark specific order menus as paid
 * @param {Object} router - Next.js router
 * @param {string} orderId - Order ID
 * @param {Object} payload - Items and payment amounts
 * @returns {Promise} Updated order
 */
export const markOrderMenusAsPaid = async (router, orderId, payload, { idempotencyKey } = {}) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.patch(
      `/admin/restaurant/${restaurantId}/order/${orderId}/mark_order_menus_paid/`,
      payload,
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
          ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    console.error('Error marking order menus as paid:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update order menu status
 * @param {Object} router - Next.js router
 * @param {string} orderId - Order ID
 * @param {Object} status - Status update data
 * @returns {Promise} Updated order
 */
export const updateOrderMenuStatus = async (router, orderId, status) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.put(
      `/admin/restaurant/${restaurantId}/order/${orderId}/update-order-menu-status/`,
      status,
      { headers: getAuthHeaders() }
    );
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Complete a pay-first order in one step (all active menus -> SERVED).
 * Used by the board's single "Terminé" button when payment_mode === 'pay_first'.
 * @param {Object} router - router
 * @param {string} orderId - Order ID
 * @returns {Promise} Completion response
 */
export const completeOrder = async (router, orderId) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.put(
      `/admin/restaurant/${restaurantId}/order/${orderId}/complete-order/`,
      {},
      { headers: getAuthHeaders() }
    );
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Cancel an order
 * @param {Object} router - Next.js router
 * @param {string} orderId - Order ID
 * @param {Object} reason - Cancellation reason
 * @returns {Promise} Cancellation response
 */
/**
 * Every cancellation of a window, told apart: voided tickets, lines removed before the
 * kitchen, lines removed after it (a loss). Same window / sessions as the cash report.
 */
export const getOrderCancellations = async ({ dateFrom, dateTo, sessionIds, serviceDay } = {}) => {
  const restaurantId = getRestaurantId();
  const params = {};
  // the evening close's service day (end-of-day hour to end-of-day hour), not the calendar day
  if (serviceDay) params.service_day = serviceDay;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (sessionIds && sessionIds.length) params.business_session_ids = sessionIds.join(',');
  const response = await apiClient.get(
    `/admin/restaurant/${restaurantId}/order/cancellations/`,
    { params, headers: getAuthHeaders() }
  );
  return response.data;
};

export const cancelOrder = async (router, orderId, reason) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.patch(
      `/admin/restaurant/${restaurantId}/order/${orderId}/cancel/`,
      reason,
      { headers: { ...getAuthHeaders(), ...managerApprovalHeader() } }
    );
    return response;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Update order employee
 * @param {Object} router - Next.js router
 * @param {string} orderId - Order ID
 * @param {string} employeeId - Employee ID
 * @returns {Promise} Updated order
 */
export const updateOrderEmployee = async (router, orderId, employeeId) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.patch(
      `/admin/restaurant/${restaurantId}/order/${orderId}/employee/`,
      { employee_id: employeeId },
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

// ============================================================================
// REFUNDS
// ============================================================================

/**
 * Preview a refund: how much the customer is refunded now + the THEORETICAL
 * deferred ODS-fee refund and the current monthly cap state. Never touches Stripe.
 * @param {Object} payload - { kind: 'total'|'items'|'amount', items?, amount?, reason? }
 * @returns {Promise} preview object (all amounts in minor units / cents)
 */
export const previewRefund = async (router, orderId, payload) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.post(
      `/admin/restaurant/${restaurantId}/order/${orderId}/refund-preview`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) handleUnauthorizedResponse(router);
    throw error;
  }
};

/**
 * Execute a customer refund (total / items+quantity / free amount).
 * @param {Object} payload - { kind, items?: [{order_menu_id, quantity}], amount?, reason? }
 * @returns {Promise} created refund summary
 */
export const createRefund = async (router, orderId, payload) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.post(
      `/admin/restaurant/${restaurantId}/order/${orderId}/refunds`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) handleUnauthorizedResponse(router);
    throw error;
  }
};

/**
 * The restaurateur's deferred ODS-fee refund ledger for a calendar month.
 * @param {string} period - 'YYYY-MM' (defaults to current month server-side)
 * @returns {Promise} { period, monthlyOdsFeeRevenue, monthlyOdsFeeRefundCap, items, ... }
 */
export const getOdsFeeRefunds = async (router, period = null) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.get(
      `/admin/restaurant/${restaurantId}/ods-fee-refunds`,
      { headers: getAuthHeaders(), params: period ? { period } : {} }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) handleUnauthorizedResponse(router);
    throw error;
  }
};

/**
 * Restaurateur self-service: request (immediately reimburse) the eligible ODS fee
 * for a month. Enforces one request per month server-side.
 * @param {string} period - 'YYYY-MM'
 * @param {{ amount?: number, note?: string }} body - amount omitted → full max
 */
export const requestOdsFeeRefund = async (router, period, { amount = null, note = null } = {}) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.post(
      `/admin/restaurant/${restaurantId}/ods-fee-refunds/request`,
      { period, amount, note },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) handleUnauthorizedResponse(router);
    throw error;
  }
};

// ── ODS-platform admin (is_support) ──────────────────────────────────────────

/** List every restaurant's ODS-fee ledger rows for a period (ODS staff only). */
export const listAllOdsFeeRefunds = async (router, period, filters = {}) => {
  try {
    const response = await apiClient.get(`/admin/ods-fee-refunds`, {
      headers: getAuthHeaders(),
      params: { period, ...filters },
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) handleUnauthorizedResponse(router);
    throw error;
  }
};

/** Run the end-of-month settlement batch for a period (optionally one restaurant). */
export const runOdsFeeBatch = async (router, period, restaurantId = null) => {
  try {
    const response = await apiClient.post(
      `/admin/ods-fee-refunds/${period}/run`,
      {},
      { headers: getAuthHeaders(), params: restaurantId ? { restaurant_id: restaurantId } : {} }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) handleUnauthorizedResponse(router);
    throw error;
  }
};

/** Retry a single FAILED ledger row. */
export const retryOdsFeeRow = async (router, ledgerId) => {
  try {
    const response = await apiClient.post(
      `/admin/ods-fee-refunds/${ledgerId}/retry`, {}, { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) handleUnauthorizedResponse(router);
    throw error;
  }
};

/** Exclude a ledger row (reason mandatory). */
export const excludeOdsFeeRow = async (router, ledgerId, reason) => {
  try {
    const response = await apiClient.post(
      `/admin/ods-fee-refunds/${ledgerId}/exclude`, { reason }, { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) handleUnauthorizedResponse(router);
    throw error;
  }
};